import type { RowSelectionState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { ScrapingJob } from '@/features/Scraping/api/dto';

type UseScrapingJobSelectionParams = {
  /** COMMERCE 필터만 적용된 전체 jobs — 선택 상태 파생용 */
  allJobs: ScrapingJob[];
  /** 검색 필터 적용된 jobs — toggleAll 범위 */
  visibleJobs: ScrapingJob[];
};

/**
 * 스크래핑 job 행 선택 상태 관리 훅
 *
 * - rowSelection 상태 관리
 * - 개별 행 토글 / 전체 선택·해제
 * - 선택된 job 목록 파생
 */
export const useScrapingJobSelection = ({
  allJobs,
  visibleJobs,
}: UseScrapingJobSelectionParams) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // 선택된 job 목록 (전체 allJobs에서 rowSelection 기반 파생)
  const selectedJobs = useMemo(
    () => allJobs.filter((j) => rowSelection[j.alias]),
    [allJobs, rowSelection],
  );

  const isAllSelected =
    visibleJobs.length > 0 &&
    visibleJobs.every((j) => rowSelection[j.alias]);
  const isIndeterminate = selectedJobs.length > 0 && !isAllSelected;

  const handleClickRow = (job: ScrapingJob) => {
    setRowSelection((prev) => {
      const next = { ...prev };
      if (next[job.alias]) {
        delete next[job.alias];
      } else {
        next[job.alias] = true;
      }
      return next;
    });
  };

  // 현재 보이는 job 전체 선택/해제
  const handleToggleAll = () => {
    if (isAllSelected) {
      setRowSelection((prev) => {
        const next = { ...prev };
        for (const job of visibleJobs) {
          delete next[job.alias];
        }
        return next;
      });
    } else {
      setRowSelection((prev) => {
        const next = { ...prev };
        for (const job of visibleJobs) {
          next[job.alias] = true;
        }
        return next;
      });
    }
  };

  return {
    rowSelection,
    selectedJobs,
    handleClickRow,
    handleToggleAll,
    isAllSelected,
    isIndeterminate,
  };
};
